import { Box } from "@mui/material";
import { LabConfig } from "../../types/lab.types";
import { CardSlot } from "./CardSlot";
import { LocalCardGrid } from "./LocalCardGrid";

function hashCode(s: string): number {
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) - hash + s.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

function getCardTransform(labId: string) {
  const h = hashCode(labId);
  return {
    rotate: ((h % 100) / 100) * 10 - 5,
    translateX: (((h >> 8) % 100) / 100) * 20 - 10,
    translateY: (((h >> 16) % 100) / 100) * 20 - 10,
  };
}

// Row height less than card height creates overlap between rows.
// Cards in later rows naturally paint on top of earlier ones.
const ROW_HEIGHT_SM = 220;
const ROW_HEIGHT_XS = 170;

interface MessyCardGridProps {
  labs: LabConfig[];
}

export function MessyCardGrid({ labs }: MessyCardGridProps) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(auto-fill, minmax(180px, 1fr))",
          sm: "repeat(auto-fill, minmax(220px, 1fr))",
        },
        gridAutoRows: { xs: ROW_HEIGHT_XS, sm: ROW_HEIGHT_SM },
        justifyItems: "center",
        width: "100%",
        maxWidth: 1000,
        mx: "auto",
        p: { xs: 1, sm: 2 },
      }}
    >
      {labs.map((lab) => {
        const { rotate, translateX, translateY } = getCardTransform(lab.id);
        return (
          <Box
            key={lab.id}
            sx={{
              width: { xs: 160, sm: 204 },
              height: { xs: 254, sm: 324 },
              transform: `rotate(${rotate}deg) translate(${translateX}px, ${translateY}px)`,
              transition: "transform 0.3s ease, filter 0.3s ease, z-index 0s",
              filter: "drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15))",
              position: "relative",
              "&:hover": {
                transform: "rotate(0deg) translate(0, -8px) scale(1.05)",
                zIndex: 20,
                filter: "drop-shadow(0 12px 32px rgba(0, 0, 0, 0.3))",
              },
            }}
          >
            <CardSlot labId={lab.id} cardUrl={lab.cardUrl!} cardScope={lab.cardScope} />
          </Box>
        );
      })}
      <LocalCardGrid />
    </Box>
  );
}
